import React, { useEffect, useState } from "react";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

const MyProfile = () => {
  const [user, setUser] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const username = sessionStorage.getItem("username");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/user/profile/${username}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    if (username) fetchProfile();
  }, [username, token]);

  const togglePasswordForm = () => {
    setShowPasswordForm((prev) => !prev);
    setMessage(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const isPasswordValid = (password) => {
    return (
      password.length >= 8 &&
      /[!@#$%^&*]/.test(password) &&
      /[0-9]/.test(password)
    );
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!newPassword || !confirmPassword) {
      setMessage("Both fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setMessage("Password must be at least 8 characters, include a number and a special character.");
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        `${API_URL}/api/user/update-password`,
        {
          email: username,
          newPassword: newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAlert("✅ Password updated successfully.");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
      setTimeout(() => setAlert(null), 3000);
    } catch (error) {
      setMessage(
        error.response?.data || "❌ Failed to update password. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div className="mt-24 text-center">Loading profile...</div>;

  return (
    <div className="mt-24 max-w-2xl mx-auto bg-white shadow-xl p-8 rounded-lg">
      <h2 className="text-2xl font-bold text-green-700 mb-6 text-center">
        🌿 My Profile
      </h2>

      {alert && (
        <div className="mb-4 text-green-600 text-center font-semibold">
          {alert}
        </div>
      )}

      <div className="space-y-4 text-gray-800">
        <p><span className="font-semibold">Username:</span> {user.username}</p>
        <p><span className="font-semibold">Name:</span> {user.name}</p>
        <p><span className="font-semibold">Phone Number:</span> {user.phoneNumber}</p>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={togglePasswordForm}
          className="bg-green-600 text-white px-5 py-2 rounded-md shadow hover:bg-green-700 transition"
        >
          {showPasswordForm ? "Cancel" : "Update Password"}
        </button>
      </div>

      {showPasswordForm && (
        <form
          onSubmit={handlePasswordUpdate}
          className="mt-6 bg-gray-50 p-4 rounded-md space-y-4 border border-gray-200"
        >
          {message && (
            <div className="text-sm text-center text-red-600">{message}</div>
          )}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              className="w-full mt-1 border rounded p-2 pr-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <span
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-9 cursor-pointer text-gray-600"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </span>
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full mt-1 border rounded p-2 pr-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 cursor-pointer text-gray-600"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </span>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
          >
            {loading ? "Updating..." : "Submit"}
          </button>
        </form>
      )}
    </div>
  );
};

export default MyProfile;
